import type { ComponentProps, ReactElement } from 'react'
import React from 'react'
import CartReviewTileBase from './_CartReviewTileBase'
import CartReviewTilePrimaryContent from './_CartReviewTilePrimaryContent'
import CartReviewTileSecondaryContent from './_CartReviewTileSecondaryContent'
import CartReviewTileImage from './_CartReviewTileImage'
import type { ImageParams } from 'components/Common/ResponsiveImage'

type InheritedCartReviewTileBaseProps = Pick<ComponentProps<typeof CartReviewTileBase>, 'to' | 'banner' | 'extension' | 'footer'>
type InheritedCartReviewTileSecondaryContentProps = Pick<ComponentProps<typeof CartReviewTileSecondaryContent>, 'price'>
type InheritedCartReviewTileImageProps = Pick<ComponentProps<typeof CartReviewTileImage>, 'label'>

export interface LoyaltyPointsEarningConfiguration {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  calculationType: 'estimate' | 'exact'
  hideStatusCredits?: boolean
}

interface Props extends InheritedCartReviewTileBaseProps, InheritedCartReviewTileSecondaryContentProps, InheritedCartReviewTileImageProps {
  imageElement?: ReactElement
  isBookable?: boolean
  saveForLater?: boolean
  className?: string
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  imageParams?: ImageParams
  /**
   * Content element to be used for the main content
   * section of the tile
   *
   * Commonly a `CartReviewTilePrimaryContent`
   */
  content: ReactElement

  /**
   * Actions element that is used to indicate what
   * actions are available for the item.
   *
   * Commonly a 'CartReviewTileActions'
   */
  actions?: ReactElement
}

function CartReviewTile({
  label,
  imageElement,
  price,
  banner,
  extension,
  footer,
  to,
  isBookable,
  saveForLater,
  className,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  content,
  actions,
}: Props) {
  return (
    <CartReviewTileBase
      banner={banner}
      extension={extension}
      footer={footer}
      to={to}
      layout={imageElement ? 'with-image' : 'without-image'}
      showOverlay={disableOverlay ? false : !isBookable || saveForLater}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
    >
      {imageElement && <CartReviewTileImage
        imageElement={imageElement}
        label={label}
      />}
      <CartReviewTilePrimaryContent>
        {content}
      </CartReviewTilePrimaryContent>
      {!hideAvailabilityDetails && <CartReviewTileSecondaryContent
        price={isBookable ? price : undefined}
      >
        {actions}
      </CartReviewTileSecondaryContent>}
    </CartReviewTileBase>
  )
}

export default CartReviewTile
